import { type AuthenticationResult } from '@azure/msal-browser'
import React from 'react'
import eqxAuthMsal from '../../shared/classes/eqxAuthMsal'
import { eqxEvent } from '../../shared/classes/eqxEventBroker'
import generateContextProvider, { ContextProviderWrapperProps } from '../../shared/hooks/GenerateContextProvider'
import { useApiContext } from './AppApi'
import { useBaseConfigContext } from './AppBaseConfig'
import { useEnvironmentContext } from './AppEnvironment'
import { useTenantContext } from './AppTenant'
import { useTenantID } from './AppTenantID'

interface AppAuth {
    msal: eqxAuthMsal
    token: string | undefined
    login: () => Promise<string>
    logout: () => Promise<void>
}

const AppAuthWrapper = (props: ContextProviderWrapperProps<AppAuth>) => {
    const AuthProvider = props.provider

    const tenantID = useTenantID()
    const tokenFromStorage = localStorage.getItem('token') ?? undefined
    const tokenFromStorageTenant = localStorage.getItem('token_tenant') ?? undefined
    const [token, setToken] = React.useState<string | undefined>(
        tokenFromStorageTenant == tenantID ? tokenFromStorage : undefined //only use the token if it's for the right tenant
    )

    const environment = useEnvironmentContext()
    const baseConfig = useBaseConfigContext()
    const tenant = useTenantContext()
    const api = useApiContext()

    const conf = {
        clientId: tenant.aadClientID,
        authority: 'https://login.microsoftonline.com/' + tenant.aadTenantID,
        //authority: `https://login.microsoftonline.com/common`,
        redirectUri: environment.domain,
        scopes: baseConfig.auth.scopes,
    }
    const msal = new eqxAuthMsal({
        auth: conf,
    })
    const setTokenInApp = (token?: string) => {
        setToken(token)
        if (token) {
            localStorage.setItem('token', token)
            localStorage.setItem('token_tenant', tenantID)
        } else {
            localStorage.removeItem('token')
            localStorage.removeItem('token_tenant')
        }
    }
    React.useEffect(() => {
        const eventId = api.events.on('response426', function (this: eqxEvent, response: Response) {
            setTokenInApp(undefined);
        })
        return () => {
            api.events.removeEvent('response426', eventId)
        }
    }, [])
    const auth = {
        msal,
        token,
        login: () => {
            return msal.login().then((token: AuthenticationResult) => {
                return api.endpoints
                    .token()
                    .execute(api, {
                        aadToken: token.accessToken,
                        version: baseConfig.version,
                    })
                    .then((response) => {
                        setTokenInApp(response.accessToken)
                        return response.accessToken
                    })
            })
        },
        logout: () => {
            return msal.logout().then(() => {
                setTokenInApp(undefined)
            })
        },
    }

    //pass auth on to the children
    return <AuthProvider value={auth}>{props.children}</AuthProvider>
}

const { ContextProvider: AppAuthProvider, useContext: useAuthContext } = generateContextProvider<AppAuth>(
    undefined,
    AppAuthWrapper
)

export { AppAuthProvider as default, useAuthContext, type AppAuth }

